/**
 * A helper utility for parsing various conditional arguments into a classname string
 */

/* - Usage Patterns
    cx('foo', 'bar'); // => 'foo bar'
    cx('foo', { bar: true }); // => 'foo bar'
    cx({ 'foo-bar': false }); // => ''
    cx({ foo: true }, { bar: true }); // => 'foo bar'
    cx({ foo: true, bar: true }); // => 'foo bar'

    // lots of arguments of various types
    cx('foo', { bar: true, duck: false }, 'baz', { quux: true }); // => 'foo bar baz quux'

    // other falsy values are just ignored
    cx(null, false, 'bar', undefined, 0, 1, { baz: null }, ''); // => 'bar 1'

    // arrays are recursively flattened
    const arr = ['b', { c: true, d: false }];
    cx('a', arr); // => 'a b c'

    // for css modules, use dynamic classnames
    cx(styles.wrapper, styles.foo && isNotTrue, styles[size]); // => 'module_wrapper module_small' when { isNotTrue: false, size: "small" } 

    // or add the styles object as a property on an conditional classnames object
    cx({ styles, wrapper: true, foo: false, [size]: true }) // => 'module_wrapper module_small' when { size: "small" } 
*/

export interface ModuleClassNames {
  readonly [className: string]: string;
}

type ClassNamesObject = {
  [className: string]: string | boolean | ModuleClassNames | undefined;
  styles?: ModuleClassNames;
};

export type ClassNameArg = string | ClassNamesObject | Array<ClassNameArg> | number | boolean | undefined | null;

function pushClasses(args: ClassNameArg[], classes: string[]): void {
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];

    if (!arg) {
      continue;
    }

    if (typeof arg === "string") {
      classes.push(arg);
    } else if (typeof arg === "number") {
      classes.push(String(arg));
    } else if (Array.isArray(arg)) {
      pushClasses(arg, classes);
    } else if (typeof arg === "object") {
      const obj = arg as ClassNamesObject;
      const styles = obj.styles;

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && key !== "styles" && obj[key]) {
          classes.push(styles ? styles[key] : key);
        }
      }
    }
  }
}

export default function cx(...args: ClassNameArg[]): string {
  const classes: string[] = [];
  pushClasses(args, classes);
  return classes.join(" ");
}
